<script lang="ts" setup>
import { VButton, VCard } from "@app/core";
import { DateTime } from "luxon";
import { computed } from "vue";
import { useRoute } from "vue-router";

import { useModal } from "@/shared/composables";
import { ALL_FILTERS } from "@/shared/config/globalFilters.config";
import { useGlobalFiltersStore } from "@/shared/stores/useGlobalFiltersStore";
import ComparisonDatePicker from "@/widgets/filters/components/ComparisonDatePicker.vue";
import GranularityFilter from "@/widgets/filters/components/GranularityFilter.vue";
import MainDatePicker from "@/widgets/filters/components/MainDatePicker.vue";

const { open } = useModal("global-filter");
const { isFilterVisible } = useGlobalFiltersStore();

const route = useRoute();

const isFiltersButtonVisible = computed(() =>
  ALL_FILTERS.some(filter => isFilterVisible(filter)),
);

const isDateControlsVisible = computed(() =>
  isFilterVisible("dateRange")
  || isFilterVisible("comparison")
  || isFilterVisible("granularity"),
);

const datePickerHelperText = computed<string>(() => {
  const date = DateTime.now().minus({ day: 2 }).toFormat("MMMM dd, yyyy");

  return `Latest Available Date ${date}`;
});
</script>

<template>
  <div
    v-if="isFiltersButtonVisible || route.meta.title"
    class="flex flex-wrap items-center gap-x-4 gap-y-3 w-full border-b border-base-300 mb-4 pb-4 "
  >
    <h1 class="text-3xl font-bold text-mainText whitespace-nowrap">
      {{ route.meta.title || "" }}
    </h1>
    <div class="flex flex-wrap items-center gap-3 flex-1 justify-end min-w-0">
      <div
        id="default-header-filters"
        class="contents"
      />
      <v-card
        v-if="isDateControlsVisible || isFiltersButtonVisible"
        class="w-fit flex-shrink-0 z-10"
      >
        <div class="flex flex-wrap items-center gap-3">
          <MainDatePicker
            v-if="isFilterVisible('dateRange')"
            class="flex-shrink-0"
          >
            <span>{{ datePickerHelperText }}</span>
          </MainDatePicker>
          <ComparisonDatePicker
            v-if="isFilterVisible('comparison')"
            :date-helper-text="datePickerHelperText"
            class="flex-shrink-0"
          >
            <span>{{ datePickerHelperText }}</span>
          </ComparisonDatePicker>
          <GranularityFilter
            v-if="isFilterVisible('granularity')"
            class="flex-shrink-0"
          />
          <div
            v-if="isDateControlsVisible && isFiltersButtonVisible"
            class="h-6 w-px bg-neutral-200"
          />
          <VButton
            v-if="isFiltersButtonVisible"
            aria-label="Global Filters"
            class="flex-shrink-0"
            icon="lucide:sliders-horizontal"
            @click="open"
          />
        </div>
      </v-card>
    </div>
  </div>
</template>
